import { useAtomValue } from 'jotai';
import {
  pageFilterAtom,
  rowsPerPageFilterAtom,
  rsimFiltersAtom,
  rsimSortingAtomWithStorage,
} from 'atoms/filters/rsim-products.ts';
import { useValidatedSearchId } from 'common/components/Filters/hooks/useValidatedSearchId.ts';
import { useSuspenseQuery } from '@tanstack/react-query';
import { RSimsData } from 'rSimInventory/constants/rsim.ts';
import { setMinimalPromiseTime } from 'common/utils/setMinimalPromiseTime.ts';
import { fetchRSims } from 'rSimInventory/RSimApi/rSimApi.ts';
import { useExtendsRSimProductsWithActionsAvailability } from 'rSimInventory/Actions/hooks/useExtendsRSimProductsWithActionsAvailability.ts';
import { useMemo } from 'react';
import { useRSimQueryKey } from 'rSimInventory/hooks/useRSimQueryKey.ts';

export const useRSimQuery = () => {
  const rowsPerPage = useAtomValue(rowsPerPageFilterAtom);
  const page = useAtomValue(pageFilterAtom);
  const sorting = useAtomValue(rsimSortingAtomWithStorage);
  const filters = useAtomValue(rsimFiltersAtom);

  const {
    searchText,
    chosenIdType,
    lastActiveProfile,
    lastUpdateTime,
    tags,
    connectionId,
    orderNumber,
    simulateOutage,
    accounts,
    productCodes,
    eidFrom,
    eidTo,
    subscriptionStatus,
    mobileNetworkOperator,
    mnoNames,
    mobileNetworkOperatorId,
  } = filters;

  const { search } = useValidatedSearchId(searchText);
  const queryKey = useRSimQueryKey();

  const queryResult = useSuspenseQuery<RSimsData>({
    queryKey: queryKey,
    queryFn: async ({ signal }) => {
      return setMinimalPromiseTime(
        fetchRSims(
          page,
          rowsPerPage,
          {
            searchText: search,
            chosenIdType,
            connectionId,
            orderNumber,
            simulateOutage,
            lastActiveProfile,
            tags,
            accounts,
            productCodes,
            lastUpdateTime,
            eidFrom,
            eidTo,
            subscriptionStatus,
            mobileNetworkOperator,
            mnoNames,
            mobileNetworkOperatorId,
          },
          sorting,
          signal,
        ),
      );
    },
  });

  const extendedRSimItems = useExtendsRSimProductsWithActionsAvailability(queryResult.data.items);

  return useMemo(
    () => ({
      queryResult: { ...queryResult, data: { ...queryResult, items: extendedRSimItems } },
      queryKey,
    }),
    [queryResult, extendedRSimItems],
  );
};
